import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { string, func, any, arrayOf, bool, shape, object } from 'prop-types'
import './Table.scss'
import TableCard from './TableCard/TableCard'
import GenericRow from './TableRow/GenericRow'
import { scrollService } from '../../services/scrollService'
import useBreakpoint from '../../../dpe-usco/services/useBreakpoint'
import TableStatus from '../../../components/subComponents/TableStatus/TableStatus'
import Header from './Header'

export const Table = props => {
  let [activeRow, setActiveRow] = useState()
  const { selectableRow = false, onRowSelect, rowSelectKey } = props
  // Use helper to derive a unique id the same way populateRows uses
  const getRowId = useCallback((item, index) => (props.structure?.rowKey ? item[props.structure.rowKey] : index), [props.structure?.rowKey])
  const [selectedIds, setSelectedIds] = useState([])
  const mountedRef = useRef(false)
  // Flag to know if selectedIds change came from an explicit user action (checkbox click)
  const userActionRef = useRef(false)

  // Clean up selected ids when data changes (remove ids no longer present)
  useEffect(() => {
    if (!selectableRow) return
    const currentIds = new Set((props.data || []).map((d, i) => getRowId(d, i)))
    setSelectedIds(prev => prev.filter(id => currentIds.has(id)))
    // Data-driven changes (like filtering / refetch) must not trigger the selection callback
    userActionRef.current = false
  }, [props.data, selectableRow, getRowId])

  // Emit selection changes
  useEffect(() => {
    if (!selectableRow || !onRowSelect) return
    if (!mountedRef.current) { mountedRef.current = true; return }
    // Fire only when the change came from explicit user interaction (row or header checkbox)
    if (!userActionRef.current) return
    const rows = (props.data || []).filter((d, i) => selectedIds.includes(getRowId(d, i)))
    let payload
    if (!rowSelectKey) {
      payload = rows
    } else if (typeof rowSelectKey === 'string') {
      payload = rows.map(r => r?.[rowSelectKey])
    } else if (typeof rowSelectKey === 'function') {
      payload = rows.map(r => rowSelectKey(r))
    } else {
      payload = rows
    }
    onRowSelect(payload)
    // Reseta a flag após notificar
    userActionRef.current = false
  }, [selectedIds, selectableRow, onRowSelect, props.data, getRowId, rowSelectKey])

  const allSelected = useMemo(() => {
    if (!selectableRow) return false
    const data = props.data || []
    if (!data.length) return false
    return data.every((d, i) => selectedIds.includes(getRowId(d, i))) && selectedIds.length > 0
  }, [selectedIds, props.data, selectableRow, getRowId])

  // Memoized Set for O(1) membership checks per row
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const toggleAll = () => {
    if (!selectableRow) return
    const data = props.data || []
    userActionRef.current = true
    if (allSelected) setSelectedIds([])
    else setSelectedIds(data.map((d, i) => getRowId(d, i)))
  }

  const toggleRow = id => {
    if (!selectableRow) return
    userActionRef.current = true
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }
  const [breakpoint] = useBreakpoint()
  const viewIsMobile = breakpoint === 'mobile'

  const { structure } = props
  const { headers, showTooltip, rowFormat, dataKeys } = structure || {}

  const populateRows = () => {
    const referenceData = props.data
    if (!referenceData || !referenceData.length) return <div>{props.statusComponent}</div>
    return referenceData.map((item, index) => {
      const uniqueID = getRowId(item, index)
      const tempProps = {
        id: uniqueID,
        rowContent: item,
        handleClick: rowClickHandler,
        isActive: uniqueID === activeRow,
        header: props.headers,
        structure: props.structure,
        customRowClass: item.customRowClass,
        index
      }
      if (props.subRowStructure) {
        tempProps.subRowStructure = props.subRowStructure
      }
      // Stable handler per ID
      const handleToggle = () => toggleRow(uniqueID)
      return <GenericRow key={uniqueID} {...tempProps} replantOrLost={props.replantOrLost} deleteMultipleScaleTickets={props.deleteMultipleScaleTickets} productionPlanId={props.productionPlanId} selectableRow={selectableRow} isRowSelected={selectedIdSet.has(uniqueID)} onToggleSelect={handleToggle} />
    })
  }

  const populateCards = () => {
    let referenceData = props.data
    if (referenceData) {
      return referenceData.map((item, index) => {
        const uniqueID = item.uniqueID
          ? item.uniqueID
          : index + '-' + (item.Name || item.ship_to_name || item.reseller_name)
        const tempProps = {
          id: uniqueID,
          rowContent: item,
          handleClick: rowClickHandler,
          isActive: uniqueID === activeRow,
          header: props.headers,
          structure: props.structure,
          index
        }
        if (props.subRowStructure) {
          tempProps.subRowStructure = props.subRowStructure
        }
        return <TableCard key={uniqueID} {...tempProps} />
      })
    } else {
      console.error('table data not found')
    }
  }

  const rowClickHandler = (rowContent, isSubmenu) => {
    const { [props.structure.rowKey]: id } = rowContent
    if (!isSubmenu) {
      if (id !== activeRow) {
        if (props.dropdownCallback) {
          props.dropdownCallback(rowContent)
        }
        setActiveRow(id)
      } else {
        setActiveRow(null)
      }
    }
  }

  const handleScroll = e => {
    scrollService(e, props.onScrollAPI)
  }

  return (
    <>
      {viewIsMobile ? (
        <div className="table ml25 mr25" onScroll={handleScroll}>
          <div className="tableHeader pl16 df fxdrc h90">
            <div className="df jcsb w100% aic h44">
              <input type="text" />
              <button>Filter</button>
            </div>
            <div className="df jcsb w100% aic h44 pl16 pr16">
              <div>{props.structure.headers[0]?.value}</div>
              <div>{props.structure.headers[1]?.value}</div>
            </div>
          </div>
          <div className="tableBody" style={props.grainReceivers && { maxHeight: '675px' }}>{populateCards()}</div>
          <div className="tableLoader">
            {props.statusComponent}
          </div>
        </div>
      ) : (
        <div
          className={`table ${selectableRow ? 'table--selectable' : ''}`}
          role="grid"
          onScroll={handleScroll}
          data-testid={props.dataTestId}
        >
          <div
            className={`tableHeader ${props.structure.hasSubRow ? 'pl60' : 'pl16'
              }`}
          >
            {headers ? <Header items={headers} showTooltip={showTooltip} rowFormat={rowFormat} dataKeys={dataKeys} selectableRow={selectableRow} allSelected={allSelected} onToggleAll={toggleAll} /> : null}
          </div>
          <div className="tableBody" style={props.grainReceivers && { maxHeight: '675px' }}>
            {!props.statusComponent && populateRows()}
          </div>

          {props.isLoadExtraData && (<div className='extraDataLoader'>
            <div className='loaderDiv'> <TableStatus status={'LOADING'} /> </div>
          </div>)}

          {!props.statusComponent && props.showYieldValue ? props.showYieldValue : null}

          {props.statusComponent && (
            <div className={`${props.statusComponentHeight || 'h150'} df jcc pt30`}>{props.statusComponent}</div>
          )}
          {props.bottomOfTable}
        </div>
      )}
    </>
  )
}
export default Table

Table.propTypes = {
  dataTestId: string,
  subRowStructure: object,
  headers: arrayOf(string),
  data: arrayOf(any),
  dropdownCallback: func,
  isLoadExtraData: bool,
  onScrollAPI: func,
  statusComponent: object,
  selectableRow: bool,
  onRowSelect: func,
  rowSelectKey: any,
  structure: shape({
    rowKey: any,
    clickable: bool,
    customMemoAreEqual: func,
    hasIcon: bool,
    hasSubRow: bool,
    headers: arrayOf(any),
    rowFormat: object
  })
}
